import {
    CountryCode,
    EnableStatus,
    HttpMethod,
    HttpPost,
    MaxParkingUnit,
    Mode,
    PeriodicReport,
    PostType,
    Snapshot,
} from '@EcamModel/model/PMC8266ParkingSpaceDetection';
import { Button, Divider, Stack, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2/Grid2';
import InformationIcon from '@pages/cameras/details/components/icon/InformationIcon';
import { useCameraDetailContext } from '@pages/cameras/hook/CameraDetailsProvider';
import { SectionsCollapsePMC8266 } from '@pages/cameras/hook/useMilesightPMC8266';
import color from '@theme/Colors';
import _ from 'lodash';
import { ReactNode, useState } from 'react';
import { BiHide, BiShow } from 'react-icons/bi';
import {
    BaseSectionCollapse,
    DataSectionHasSubItem,
    SectionHasSubItemHeader,
    SectionHasSubItemProps,
    SectionNotSubItem,
} from '../../../ptz/milesight/components/BasicInfoMilesightCameraPTZ';
import BaseTableComponent, { TableHeaderConfig } from './BaseTableComponent';

const modeMapping: { [key in Mode]: string } = {
    [Mode.Occupancy]: 'Occupancy detection',
    [Mode.Parking]: 'Parking detection',
};

const enableStatusMapping: { [key in EnableStatus]: string } = {
    [EnableStatus.Disabled]: 'Disabled',
    [EnableStatus.Enabled]: 'Enabled',
};

const periodicReportMapping: { [key in PeriodicReport]: string } = {
    [PeriodicReport.Disabled]: 'Disabled',
    [PeriodicReport.Enabled]: 'Enabled',
};

const snapshotMapping: { [key in Snapshot]: string } = {
    [Snapshot.Disabled]: 'Disabled',
    [Snapshot.Enabled]: 'Enabled',
};

const httpMethodMapping: { [key in HttpMethod]: string } = {
    [HttpMethod.Get]: 'Get',
    [HttpMethod.Post]: 'Post',
};

const postTypeMapping: { [key in PostType]: string } = {
    [PostType.HTTP]: 'HTTP',
    [PostType.TCP]: 'TCP',
    [PostType.MQTT]: 'MQTT',
};

const maxParkingUnitMapping: { [key in MaxParkingUnit]: string } = {
    [MaxParkingUnit.Minutes]: 'Minutes',
    [MaxParkingUnit.Hours]: 'Hours',
    [MaxParkingUnit.Days]: 'Days',
};

export const countryCodeMapping: { [key in CountryCode]: string } = {
    [CountryCode.General]: 'General',
    [CountryCode.Albania]: 'Albania',
    [CountryCode.Armenia]: 'Armenia',
    [CountryCode.Austria]: 'Austria',
    [CountryCode.Azerbaijan]: 'Azerbaijan',
    [CountryCode.Belarus]: 'Belarus',
    [CountryCode.Belgium]: 'Belgium',
    [CountryCode.BosniaAndHerzegovina]: 'Bosnia and Herzegovina',
    [CountryCode.Bulgaria]: 'Bulgaria',
    [CountryCode.Croatia]: 'Croatia',
    [CountryCode.Cyprus]: 'Cyprus',
    [CountryCode.Czech]: 'Czech',
    [CountryCode.Denmark]: 'Denmark',
    [CountryCode.Estonia]: 'Estonia',
    [CountryCode.Finland]: 'Finland',
    [CountryCode.France]: 'France',
    [CountryCode.Georgia]: 'Georgia',
    [CountryCode.Germany]: 'Germany',
    [CountryCode.Greece]: 'Greece',
    [CountryCode.Hungary]: 'Hungary',
    [CountryCode.Iceland]: 'Iceland',
    [CountryCode.Ireland]: 'Ireland',
    [CountryCode.Italy]: 'Italy',
    [CountryCode.Kazakhstan]: 'Kazakhstan',
    [CountryCode.Kyrgyzstan]: 'Kyrgyzstan',
    [CountryCode.Latvia]: 'Latvia',
    [CountryCode.Lithuania]: 'Lithuania',
    [CountryCode.Luxembourg]: 'Luxembourg',
    [CountryCode.Macedonia]: 'Macedonia',
    [CountryCode.Malta]: 'Malta',
    [CountryCode.Moldova]: 'Moldova',
    [CountryCode.Montenegro]: 'Montenegro',
    [CountryCode.Netherlands]: 'Netherlands',
    [CountryCode.Norway]: 'Norway',
    [CountryCode.Poland]: 'Poland',
    [CountryCode.Portugal]: 'Portugal',
    [CountryCode.Romania]: 'Romania',
    [CountryCode.Russian]: 'Russian',
    [CountryCode.Serbia]: 'Serbia',
    [CountryCode.Slovakia]: 'Slovakia',
    [CountryCode.Slovenia]: 'Slovenia',
    [CountryCode.Spain]: 'Spain',
    [CountryCode.Sweden]: 'Sweden',
    [CountryCode.Switzerland]: 'Switzerland',
    [CountryCode.Tajikistan]: 'Tajikistan',
    [CountryCode.Turkey]: 'Turkey',
    [CountryCode.Turkmenistan]: 'Turkmenistan',
    [CountryCode.UK]: 'UK',
    [CountryCode.Ukraine]: 'Ukraine',
    [CountryCode.Uzbekistan]: 'Uzbekistan',
    [CountryCode.VaticanCity]: 'Vatican City',
};

type Props = {
    isOpenCollapse: { [key in SectionsCollapsePMC8266]: boolean };
    handleToggleCollapse: (section: SectionsCollapsePMC8266) => void;
};

const lprAreaHeaders: TableHeaderConfig[] = [
    { label: 'Name', align: 'left' },
    { label: 'PlgX', align: 'left' },
    { label: 'PlgY', align: 'left' },
];

export default function ParkingSpaceDetectionPMC8266(props: Props) {
    const { camera } = useCameraDetailContext();

    const [buttonSelected, setButtonSelected] = useState<number>(1);
    const [showPassword, setShowPassword] = useState<boolean>(false);

    const PMC8266ParkingSpaceDetections = camera?.PMC8266Camera?.PMC8266CameraConfigs?.ParkingSpaceDetection;

    const handleButtonClick = (index: number) => {
        setButtonSelected(index);
    };

    const selectedHttpPost = PMC8266ParkingSpaceDetections?.HttpPost?.[buttonSelected - 1];

    const snapshotInfo = {
        Url: selectedHttpPost?.Url || '',
        Enable: selectedHttpPost?.Enable,
        Password: selectedHttpPost?.Password || '',
        Method: selectedHttpPost?.Method,
        Snapshot: selectedHttpPost?.Snapshot,
        Username: selectedHttpPost?.Username || '',
    } as HttpPost;

    const PMHeader: DataSectionHasSubItem[] = [
        {
            label: 'Application mode:',
            value: <Typography>{modeMapping[PMC8266ParkingSpaceDetections?.Mode!]}</Typography>,
        },
        {
            label: 'Enable detection:',
            value: <Typography>{enableStatusMapping[PMC8266ParkingSpaceDetections?.Enable!]}</Typography>,
        },
        ...(PMC8266ParkingSpaceDetections?.Mode === Mode.Occupancy
            ? [
                  {
                      label: (
                          <Stack direction={'row'} spacing={1} alignItems={'center'}>
                              <Typography>Parking image enhancement</Typography>
                              <Tooltip
                                  placement="top"
                                  title="Custom Image Parameters may not take effect as configured while this mode enabled"
                              >
                                  <InformationIcon />
                              </Tooltip>
                              :
                          </Stack>
                      ) as ReactNode,
                      value: (
                          <Typography>{PMC8266ParkingSpaceDetections?.ImageEnhancement ? 'True' : 'False'}</Typography>
                      ),
                  },
              ]
            : [
                  {
                      label: 'Country / Region:',
                      value: <Typography>{countryCodeMapping[PMC8266ParkingSpaceDetections?.Country!]}</Typography>,
                  },
              ]),
    ];

    const PMDetectSettings: DataSectionHasSubItem[] = [
        {
            label: 'Occupancy determination:',
            value: <Typography>{PMC8266ParkingSpaceDetections?.OccupancyDuration!}</Typography>,
        },
        {
            label: 'Departure determination:',
            value: <Typography>{PMC8266ParkingSpaceDetections?.DepartureDuration!}</Typography>,
        },
        {
            label: 'LPR area:',
            value:
                PMC8266ParkingSpaceDetections?.LprArea && PMC8266ParkingSpaceDetections.LprArea.length > 0 ? (
                    <BaseTableComponent
                        headers={lprAreaHeaders}
                        data={PMC8266ParkingSpaceDetections.LprArea.filter(
                            (area) => area.Name && area.Name.trim() !== ''
                        )}
                        renderRow={(area, index) => (
                            <TableRow key={index}>
                                <TableCell>{area.Name}</TableCell>
                                <TableCell>{area.PlgX}</TableCell>
                                <TableCell>{area.PlgY}</TableCell>
                            </TableRow>
                        )}
                        size="small"
                        emptyMessage="No LPR areas configured"
                    />
                ) : (
                    'No LPR areas configured'
                ),
        },
    ];

    const PMReportSettings: DataSectionHasSubItem[] = [
        {
            label: 'Report protocol:',
            value: <Typography>{postTypeMapping[PMC8266ParkingSpaceDetections?.PostType!]}</Typography>,
        },
        ...(PMC8266ParkingSpaceDetections?.PostType === PostType.HTTP
            ? [
                  // HTTP
                  {
                      label: 'URL:',
                      value: (
                          <Stack direction={'row'} spacing={2}>
                              {PMC8266ParkingSpaceDetections?.HttpPost?.map((value, index) => (
                                  <Button
                                      key={index}
                                      variant="contained"
                                      size="small"
                                      onClick={() => handleButtonClick(index + 1)}
                                      sx={{
                                          backgroundColor: 'transparent',
                                          border:
                                              buttonSelected === index + 1
                                                  ? `1px solid ${color.success}`
                                                  : `1px solid ${color.grey200}`,
                                          minWidth: '50px',
                                          '&:hover': {
                                              backgroundColor: buttonSelected === index + 1 ? '#E5F5ED' : color.grey200,
                                              borderColor: buttonSelected === index + 1 ? color.success : color.grey200,
                                          },
                                      }}
                                  >
                                      <Typography textAlign={'center'} fontSize={12}>
                                          {index + 1}
                                      </Typography>
                                  </Button>
                              ))}
                          </Stack>
                      ),
                  },
                  {
                      label: '',
                      value: <Typography>{snapshotInfo.Url}</Typography>,
                  },
                  {
                      label: 'Enable:',
                      value: <Typography>{enableStatusMapping[selectedHttpPost?.Enable!]}</Typography>,
                  },
                  {
                      label: 'HTTP Method:',
                      value: <Typography>{httpMethodMapping[selectedHttpPost?.Method!]}</Typography>,
                  },
                  {
                      label: 'Snapshot:',
                      value: <Typography>{snapshotMapping[selectedHttpPost?.Snapshot!]}</Typography>,
                  },
                  {
                      label: 'User name:',
                      value: <Typography>{snapshotInfo.Username}</Typography>,
                  },
                  {
                      label: 'Password:',
                      value: (
                          <Stack
                              direction={'row'}
                              justifyContent={'space-between'}
                              width={'100%'}
                              alignItems={'center'}
                          >
                              <Typography>
                                  {showPassword ? snapshotInfo.Password : snapshotInfo.Password?.replace(/./g, '*')}
                              </Typography>

                              {_.isNil(snapshotInfo.Password) && (
                                  <>
                                      {showPassword ? (
                                          <BiHide
                                              fontSize={18}
                                              color="#85858A"
                                              cursor="pointer"
                                              onClick={() => setShowPassword(false)}
                                          />
                                      ) : (
                                          <BiShow
                                              fontSize={18}
                                              color="#85858A"
                                              cursor="pointer"
                                              onClick={() => setShowPassword(true)}
                                          />
                                      )}
                                  </>
                              )}
                          </Stack>
                      ),
                  },
              ]
            : PMC8266ParkingSpaceDetections?.PostType === PostType.MQTT
            ? [
                  // MQTT
                  {
                      label: 'Enable:',
                      value: (
                          <Typography>{enableStatusMapping[PMC8266ParkingSpaceDetections?.MQTT.Enable!]}</Typography>
                      ),
                  },
                  {
                      label: 'Host:',
                      value: <Typography>{PMC8266ParkingSpaceDetections?.MQTT.Host}</Typography>,
                  },
                  {
                      label: 'Port:',
                      value: <Typography>{PMC8266ParkingSpaceDetections?.MQTT.Port}</Typography>,
                  },
                  {
                      label: 'Topic:',
                      value: <Typography>{PMC8266ParkingSpaceDetections?.MQTT.Topic}</Typography>,
                  },
                  {
                      label: 'Snapshot:',
                      value: <Typography>{snapshotMapping[PMC8266ParkingSpaceDetections?.MQTT.Snapshot!]}</Typography>,
                  },
                  {
                      label: 'User name:',
                      value: <Typography>{PMC8266ParkingSpaceDetections?.MQTT.User}</Typography>,
                  },
                  {
                      label: 'Password:',
                      value: (
                          <Stack
                              direction={'row'}
                              justifyContent={'space-between'}
                              width={'100%'}
                              alignItems={'center'}
                          >
                              <Typography>
                                  {showPassword
                                      ? PMC8266ParkingSpaceDetections?.MQTT.Password
                                      : PMC8266ParkingSpaceDetections?.MQTT.Password?.replace(/./g, '*')}
                              </Typography>

                              {_.isNil(PMC8266ParkingSpaceDetections?.MQTT.Password) && (
                                  <>
                                      {showPassword ? (
                                          <BiHide
                                              fontSize={18}
                                              color="#85858A"
                                              cursor="pointer"
                                              onClick={() => setShowPassword(false)}
                                          />
                                      ) : (
                                          <BiShow
                                              fontSize={18}
                                              color="#85858A"
                                              cursor="pointer"
                                              onClick={() => setShowPassword(true)}
                                          />
                                      )}
                                  </>
                              )}
                          </Stack>
                      ),
                  },
              ]
            : [
                  // TCP
                  {
                      label: 'Camera report port:',
                      value: <Typography>{PMC8266ParkingSpaceDetections?.TcpPort}</Typography>,
                  },
              ]),
        // COMMON
        {
            label: 'Periodic report:',
            value: <Typography>{periodicReportMapping[PMC8266ParkingSpaceDetections?.PeriodicReport!]}</Typography>,
        },
        {
            label: 'Period:',
            value: <Typography>{PMC8266ParkingSpaceDetections?.Period}</Typography>,
        },
    ];

    const PMSectionsAlarmSettings: SectionHasSubItemProps[] = [
        {
            title: 'Alarm trigger thresholds',
            data: [
                {
                    label: 'Timeout alarm:',
                    value: <Typography>{snapshotMapping[PMC8266ParkingSpaceDetections?.TimeoutAlarm!]}</Typography>,
                },
                {
                    label: 'Max. parking time:',
                    value: (
                        <Typography>{`${PMC8266ParkingSpaceDetections?.MaxParkingTime} ${
                            maxParkingUnitMapping[PMC8266ParkingSpaceDetections?.MaxParkingUnit!]
                        }`}</Typography>
                    ),
                },
            ],
        },
        {
            title: 'Alarm action',
            data: [
                {
                    label: 'Send to platform:',
                    value: (
                        <Typography>{`${
                            snapshotMapping[PMC8266ParkingSpaceDetections?.SendToPlatform!]
                        } (Same configuration with Report Settings)`}</Typography>
                    ),
                },
            ],
        },
    ];

    const handleEdit = () => {
        console.log('Edit button clicked');
    };

    return (
        <Stack id="parking_management" border={'1px solid #E4E7EC'} borderRadius={2} flex={1}>
            <SectionHasSubItemHeader id="" title="Parking management" onEdit={handleEdit} />

            <Divider
                orientation="vertical"
                sx={{
                    borderBottomWidth: 1,
                    borderColor: '#E4E7EC',
                    mx: 2,
                }}
            />

            <Stack width={'100%'} padding={'16px 16px'} bgcolor={color.white} gap={2}>
                {PMHeader.map((item, index) => {
                    return (
                        <Grid key={index} xs={12} container>
                            <Grid xs={5}>
                                <Typography>{item.label}</Typography>
                            </Grid>
                            <Grid xs={7}>{item.value}</Grid>
                        </Grid>
                    );
                })}
            </Stack>

            <SectionNotSubItem
                header="Detect settings"
                name="detection_settings"
                statusList={PMDetectSettings}
                isOpen={props.isOpenCollapse.detection_settings}
                onToggleCollapse={() => {
                    props.handleToggleCollapse(SectionsCollapsePMC8266.DetectionSettings);
                }}
            />

            <SectionNotSubItem
                header="Report settings"
                name="report_settings"
                statusList={PMReportSettings}
                isOpen={props.isOpenCollapse.report_settings}
                onToggleCollapse={() => {
                    props.handleToggleCollapse(SectionsCollapsePMC8266.ReportSettings);
                }}
            />

            <BaseSectionCollapse
                title="Alarm Settings"
                id="alarm_settings"
                isOpen={props.isOpenCollapse.alarm_settings}
                onToggle={() => {
                    props.handleToggleCollapse(SectionsCollapsePMC8266.AlarmSettings);
                }}
                sections={PMSectionsAlarmSettings}
            />
        </Stack>
    );
}
